import React from 'react';
import { Link } from 'react-router-dom';
import {
    COUNTRY_COUNT,
    ROUTE_PAGE_ABOUT,
    ROUTE_PAGE_COUNTRY_PROFILE
} from '../../../constants';
import { useAppState } from '../../../hooks/use-app-state.hook';
import { getTaxonomyName, toCountryProfileList } from '../../../utils';
import Box from '../../layout/box/box.component';
import Content from '../../layout/content/content.component';
import TargetAndRepresentationDonuts from '../../partials/target-and-representation-donuts/target-and-representation-donuts.component';
import QuotaTypeChart from '../../partials/quota-type-chart/quota-type-chart.component';
import { Cell, Grid } from '../../layout/grid/grid.component';
import Icon from '../../layout/icon/icon.component';
import Page from '../../layout/page/page.component';
import Spacer from '../../layout/spacer/spacer.component';
import {
    homeContentBoxBodyClassName,
    homeContentBoxFooterLink,
    homeContentBoxFooterLinkIconClassName,
    homeContentBoxLClassName,
    homeContentBoxMClassName,
    homeContentBoxSClassName,
    homeContentBoxSmallTitle,
    homeContentBoxTitle,
    homeContentClassName,
    homeContentCountryDataClassName,
    homePageSpecificProfilesButtonClassName,
    homePageSpecificProfilesIconClassName,
    homeContentFloatingNoteClassName,
    homePageChartWrapperClassName,
    homeTitleImageClassName,
    homeImageWrapperClassName,
    homeBackgroundImageClassName
} from './home-page.style';
import QuotaTypeNotes from '../../partials/quota-type-notes/quota-type-notes.component';
import { backgroundLogo, logoLarge } from '../../../assets';

const smallTitle = (text: string) => (
    <div className={ homeContentBoxSmallTitle }>
        { text }
    </div>
);

const title = (text: string) => (
    <div className={ homeContentBoxTitle }>
        { text }
    </div>
);

const body = (children: React.ReactNode) => (
    <div className={ homeContentBoxBodyClassName }>
        { children }
    </div>
);

const footerLink = (
    text: string,
    link: string
) => (
    <div className={ homeContentBoxFooterLink }>
        <Link to={ link }>
            { text }
            <Icon
                name='dropdown'
                className={ homeContentBoxFooterLinkIconClassName }/>
        </Link>
    </div>
);

const HomePage: React.FC = () => {
    const [{
        taxonomies,
    }, {
        getPage,
        getCountryProfileResultList,
    }] = useAppState();
    const profiles = toCountryProfileList(
        getCountryProfileResultList({
            localLevel: false,
        })
    );
    const profilesLocalLevel = toCountryProfileList(
        getCountryProfileResultList({
            localLevel: true,
        })
    );
    const featuredCountryProfile = profiles[Math.floor(Math.random() * profiles.length)];
    const pageData = getPage('Home');

    return (
        <Page vCentered>
            <div className={ homeContentClassName }>
                <Content>
                    <Grid>
                        <Cell size={ 12 } sizeDesktop={ 4 }>
                            <Box className={ homeContentBoxLClassName }>
                                { pageData && (
                                    <>
                                        <div className={ homeImageWrapperClassName }>
                                            <img
                                                className={ homeBackgroundImageClassName }
                                                src={ backgroundLogo }
                                                alt=''/>
                                            <img
                                                className={ homeTitleImageClassName }
                                                src={ logoLarge }
                                                alt='Gender Quota Portal'/>
                                        </div>
                                        { body(
                                            <Spacer>
                                                <div dangerouslySetInnerHTML={ {__html: pageData.html} }/>
                                            </Spacer>
                                        )}
                                        { footerLink('More', ROUTE_PAGE_ABOUT) }
                                    </>
                                ) }
                            </Box>
                        </Cell>
                        <Cell size={ 12 } sizeDesktop={ 4 }>
                            <Box className={ homeContentBoxLClassName }>
                                { smallTitle('Featured analysis') }
                                { title('Quota type') }
                                { body(
                                    <div className={ homePageChartWrapperClassName }>
                                        <div className={ homeContentFloatingNoteClassName }>
                                            <QuotaTypeNotes countryCount={ COUNTRY_COUNT }/>
                                        </div>
                                        <QuotaTypeChart
                                            profiles={ profiles }
                                            profilesLocalLevel={ profilesLocalLevel }
                                            countryCount={ COUNTRY_COUNT }/>
                                    </div>
                                )}
                            </Box>
                        </Cell>
                        <Cell size={ 12 } sizeDesktop={ 4 }>
                            <Box className={ homeContentBoxMClassName }>
                                { smallTitle('Featured country profile') }
                                { featuredCountryProfile && title(getTaxonomyName(taxonomies, 'countries', featuredCountryProfile.countryId)) }
                                { featuredCountryProfile && body(
                                    <Spacer>
                                        <TargetAndRepresentationDonuts countryProfile={ featuredCountryProfile }/>
                                        <div className={ homeContentCountryDataClassName }>
                                            <strong>Electoral system</strong>
                                            <span>{ getTaxonomyName(taxonomies, 'electoralSystemTypes', featuredCountryProfile.electoralSystemTypeId) }</span>
                                            <strong>Electoral system sub-type</strong>
                                            <span>{ featuredCountryProfile.electoralSystemSubType }</span>
                                            <strong>Type of quota</strong>
                                            { featuredCountryProfile.typeOfQuotaIds.map((typeOfQuotaId, index) => (
                                                <span key={ index }>
                                                    { getTaxonomyName(taxonomies, 'typesOfQuota', typeOfQuotaId) }
                                                </span>
                                            )) }
                                        </div>
                                    </Spacer>
                                )}
                                { featuredCountryProfile && footerLink('View full country profile', ROUTE_PAGE_COUNTRY_PROFILE + '?countryId=' + featuredCountryProfile.countryId) }
                            </Box>
                            <Box className={ homeContentBoxSClassName } transparent>
                                { body(
                                    <Spacer>
                                        <Link to={ ROUTE_PAGE_COUNTRY_PROFILE } className={ homePageSpecificProfilesButtonClassName }>
                                            Explore country specific profiles
                                            <Icon name='magnifyingGlass' className={ homePageSpecificProfilesIconClassName }/>
                                        </Link>
                                    </Spacer>
                                ) }
                            </Box>
                        </Cell>
                    </Grid>
                </Content>
            </div>
        </Page>
    );
};

export default HomePage;
